<template>
  <div>
    <v-row class="cal-title" no-gutters align="center">
      <v-btn
        depressed
        class="ml-3 mr-3"
        color="grey lighten-4"
        small
        @click="year = parseInt($moment().format('YYYY'))"
      >
        {{ $t('message.today') }}
      </v-btn>
      <div>
        <div class="title">{{ year }}</div>
      </div>
      <v-spacer />
      <div class="mr-3">
        <v-btn :disabled="year === parseInt($moment(showDate).format('YYYY'))" icon @click="subtractYear()">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon @click="addYear()">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </v-row>
    <v-divider />
    <v-row no-gutters>
      <v-col
        v-for="monthIndex in 12"
        :key="monthIndex"
        class="cal-col"
        :class="{
          'border-right': monthIndex % 3 !== 0,
          'border-bottom': monthIndex < 10,
          today: isToday(monthIndex, year)
        }"
        cols="4"
      >
        <template v-if="getEvent(monthIndex, year)">
          <div class="text-center text-capitalize-first text-truncate pt-3">
            {{ $moment.months(monthIndex - 1) }}
          </div>
          <v-row
            no-gutters
            :justify="
              getEvent(monthIndex, year).isStart && getEvent(monthIndex, year).isEnd
                ? 'center'
                : getEvent(monthIndex, year).isStart
                ? 'end'
                : getEvent(monthIndex, year).isEnd
                ? 'start'
                : 'center'
            "
          >
            <div
              :style="{ width: getEvent(monthIndex, year).percentage + '%' }"
              :class="
                (getEvent(monthIndex, year).isStart ? ' is-start ' : '') +
                  (getEvent(monthIndex, year).isEnd ? ' is-end ' : '') +
                  getEvent(monthIndex, year).classes
              "
              class="cal-status mt-3 text-center px-1 text-truncate"
              @click="$emit('click-event', getEvent(monthIndex, year))"
            >
              <template v-if="getEvent(monthIndex, year).percentage > 30">{{
                getEvent(monthIndex, year).title
              }}</template>
            </div>
          </v-row>
        </template>
        <div
          v-else
          :class="{ 'day-empty': isClickable(monthIndex, year) }"
          @click="isClickable(monthIndex, year) ? clickMonth(monthIndex, year) : null"
        >
          <div class="text-center text-capitalize-first pt-3" :class="{ 'grey--text': !isClickable(monthIndex, year) }">
            {{ $moment.months(monthIndex - 1) }}
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: 'YearCalendar',
  props: {
    showDate: {
      type: Date,
      required: true
    },
    events: {
      type: Array,
      required: false,
      default: null
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    minStartDate: {
      type: Date,
      required: false,
      default: new Date()
    }
  },

  data() {
    return {
      year: parseInt(this.$moment(this.showDate).format('YYYY'))
    }
  },

  methods: {
    subtractYear() {
      if (this.year > parseInt(this.$moment(this.showDate).format('YYYY'))) {
        this.year--
      }
    },
    addYear() {
      this.year++
    },

    getEvent(month, year) {
      let givenDate = this.$moment({
        year: year,
        month: month - 1
      })
      let monthEvent = null
      this.events.forEach(event => {
        let startDate = this.$moment(event.startDate)
        let endDate = this.$moment(event.endDate)
        let daysInMonth = givenDate.daysInMonth()
        let startDay = parseInt(this.$moment(event.startDate).format('D'))
        let endDay = parseInt(this.$moment(event.endDate).format('D'))

        if (
          givenDate.set({ hour: 0, minute: 1 }).isBetween(
            startDate
              .clone()
              .startOf('month')
              .set({ hour: 0, minute: 0 }),
            endDate
              .clone()
              .endOf('month')
              .set({ hour: 0, minute: 0 })
          )
        ) {
          monthEvent = event
          monthEvent.isStart = false
          monthEvent.isEnd = false
          monthEvent.percentage = 100

          if (
            startDate.format('YYYY-MM') !== givenDate.format('YYYY-MM') &&
            endDate.format('YYYY-MM') !== givenDate.format('YYYY-MM')
          ) {
            monthEvent = event
            monthEvent.isStart = false
            monthEvent.isEnd = false
            monthEvent.percentage = 100
          } else if (startDate.format('YYYY-MM') === endDate.format('YYYY-MM')) {
            monthEvent.isStart = true
            monthEvent.isEnd = true
          } else if (!givenDate.set({ hour: 0, minute: 1 }).isBetween(startDate, endDate)) {
            monthEvent.isStart = true
            monthEvent.isEnd = false
            monthEvent.percentage = ((daysInMonth - startDay) / daysInMonth) * 100
          } else {
            monthEvent.isStart = false
            monthEvent.isEnd = true
            monthEvent.percentage = monthEvent.percentage = (endDay / daysInMonth) * 100
          }
        }
      })
      return monthEvent
    },
    isClickable(month, year) {
      if (this.disabled) {
        return false
      }
      let date = this.$moment({
        year: year,
        month: month - 1,
        day: 1
      }).toDate()

      return (
        (date > new Date() &&
          date <
            this.$moment()
              .add(1, 'year')
              .toDate()) ||
        this.$moment().format('YYYY-M') === year + '-' + month
      )
    },

    clickMonth(month, year) {
      if (month === parseInt(this.$moment().format('M')) && year === parseInt(this.$moment().format('YYYY'))) {
        this.$emit('click-date', this.minStartDate)
      } else {
        this.$emit('click-date', new Date(year + '-' + month + '-1'))
      }
    },

    isToday(month, year) {
      return month === parseInt(this.$moment().format('M')) && year === parseInt(this.$moment().format('YYYY'))
    }
  }
}
</script>

<style lang="scss" scoped>
.cal-title {
  height: 60px;
}
.cal-col {
  height: 90px;
  &.today {
    background-color: #f5f5f5;
  }
}
.cal-status {
  height: 23px;
  cursor: pointer;
  font-size: 0.8em;
  padding-top: 2px;
  &:hover {
    opacity: 0.8;
  }
  &.is-start {
    margin-left: 0;
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
  }

  &.is-end {
    margin-right: 0;
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
  }
  &.payment-pending {
    opacity: 0.5;
  }
}
.day-empty {
  width: 100%;
  height: 100%;
  cursor: pointer;
  &:hover {
    background-color: #eeeeee;
  }
}
</style>
